import { useEffect, useState } from "react";

interface PartnerStat { label: string; value: string; }
interface KeyService { title: string; desc: string; }
interface Achievement { year: string; text: string; }

export interface PartnerType {
  id: number;
  name: string;
  sector: string;
  founded: string;
  shortDesc: string;
  fullDesc: string;
  longDesc: string;
  since: string;
  projects: number;
  website: string;
  heroImage: string;
  tags: string[];
  stats: PartnerStat[];
  certifications: string[];
  keyServices: KeyService[];
  achievements: Achievement[];
}

interface Props {
  partner: PartnerType;
  onClose: () => void;
}

type Tab = "genel" | "uzmanlik" | "basarilar";

export default function PartnerDetailModal({ partner, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("genel");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "genel", label: "Genel Bakış", icon: "ri-information-line" },
    { id: "uzmanlik", label: "Uzmanlıklar", icon: "ri-tools-line" },
    { id: "basarilar", label: "Başarılar", icon: "ri-trophy-line" },
  ];

  const partnershipYears = new Date().getFullYear() - parseInt(partner.since);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm"></div>

      {/* Modal */}
      <div
        className="relative z-10 bg-[#F1F5F9] border border-[#E2E8F0] w-full max-w-3xl max-h-[92vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero Image Header */}
        <div className="relative h-48 overflow-hidden flex-shrink-0">
          <img
            src={partner.heroImage}
            alt={partner.name}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F1F5F9] via-[#F1F5F9]/60 to-transparent"></div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#3B82F6] hover:border-[#3B82F6] transition-all duration-200 cursor-pointer"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* Partner Name on Image */}
          <div className="absolute bottom-0 left-0 right-0 px-8 pb-5">
            <span className="inline-block text-[#64748B] text-xs tracking-[0.3em] uppercase mb-1.5">{partner.sector}</span>
            <h2 className="text-[#0F172A] font-black text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em" }}>
              {partner.name}
            </h2>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex-shrink-0 grid grid-cols-3 gap-px bg-[#E2E8F0] border-b border-[#E2E8F0]">
          {partner.stats.map((stat, i) => (
            <div key={i} className="bg-[#F1F5F9] px-5 py-4 text-center">
              <p className="text-[#0F172A] font-black text-xl mb-0.5">{stat.value}</p>
              <p className="text-[#64748B] text-xs tracking-wide">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-[#E2E8F0]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-xs font-semibold tracking-widest uppercase transition-all duration-200 cursor-pointer flex-1 justify-center ${
                activeTab === tab.id
                  ? "text-[#0F172A] border-b-2 border-[#3B82F6]"
                  : "text-[#64748B] hover:text-[#475569]"
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-7 md:p-8">

          {/* Tab: Genel Bakış */}
          {activeTab === "genel" && (
            <div className="space-y-7">
              {/* Partnership Badge */}
              <div className="flex items-center gap-4 p-4 bg-[#FFFFFF] border border-[#E2E8F0]">
                <div className="w-11 h-11 flex items-center justify-center border border-[#94A3B8] bg-[#F1F5F9]">
                  <i className="ri-links-line text-[#3B82F6] text-xl"></i>
                </div>
                <div>
                  <p className="text-[#0F172A] text-sm font-semibold">
                    {partner.since} yılından bu yana stratejik ortak
                  </p>
                  <p className="text-[#64748B] text-xs">
                    {partnershipYears} yıllık kesintisiz işbirliği · Kuruluş: {partner.founded}
                  </p>
                </div>
                <a
                  href={partner.website}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="whitespace-nowrap ml-auto flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#334155] border border-[#E2E8F0] hover:border-[#3B82F6] px-3 py-2 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  <span className="hidden sm:inline">Web Sitesi</span>
                </a>
              </div>

              {/* Long Description */}
              <div>
                <h4 className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Ortaklık Hakkında</h4>
                <div className="space-y-4">
                  {partner.longDesc.split("\n\n").map((para, i) => (
                    <p key={i} className="text-[#475569] text-sm leading-relaxed">{para}</p>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <h4 className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase mb-3">Hizmet Etiketleri</h4>
                <div className="flex flex-wrap gap-2">
                  {partner.tags.map((tag) => (
                    <span key={tag} className="text-xs text-[#334155] border border-[#E2E8F0] bg-[#FFFFFF] px-3 py-1.5 tracking-wide">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tab: Uzmanlıklar */}
          {activeTab === "uzmanlik" && (
            <div className="space-y-7">
              {/* Key Services */}
              <div>
                <h4 className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase mb-5">Temel Hizmet Alanları</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {partner.keyServices.map((service, i) => (
                    <div key={i} className="bg-[#FFFFFF] border border-[#E2E8F0] p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-1 h-4 bg-[#3B82F6] flex-shrink-0"></div>
                        <h5 className="text-[#0F172A] text-sm font-semibold">{service.title}</h5>
                      </div>
                      <p className="text-[#64748B] text-xs leading-relaxed">{service.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div>
                <h4 className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase mb-4">Sertifikalar & Belgeler</h4>
                <div className="space-y-2">
                  {partner.certifications.map((cert, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border border-[#E2E8F0] bg-[#FFFFFF]">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-[#3B82F6] text-sm"></i>
                      </div>
                      <span className="text-[#334155] text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tab: Başarılar */}
          {activeTab === "basarilar" && (
            <div>
              <h4 className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase mb-7">Ortaklık Zaman Çizelgesi</h4>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-[35px] top-0 bottom-0 w-px bg-[#E2E8F0]"></div>

                <div className="space-y-0">
                  {partner.achievements.map((ach, i) => (
                    <div key={i} className="relative flex gap-6 pb-8 last:pb-0">
                      {/* Year Circle */}
                      <div className="flex-shrink-0 w-[70px] flex flex-col items-center">
                        <div className="w-11 h-11 flex items-center justify-center bg-[#FFFFFF] border border-[#E2E8F0] z-10 relative">
                          <span className="text-[#3B82F6] font-black text-xs">{ach.year}</span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 pt-2.5 pb-4 border-b border-[#E2E8F0] last:border-b-0">
                        <p className="text-[#475569] text-sm leading-relaxed">{ach.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-7 py-5 border-t border-[#E2E8F0] flex gap-3 bg-[#F1F5F9]">
          <button
            onClick={onClose}
            className="whitespace-nowrap flex-1 py-3 border border-[#E2E8F0] text-[#334155] text-xs font-semibold tracking-widest uppercase hover:bg-[#3B82F6] hover:border-[#3B82F6] hover:text-white transition-all duration-300 cursor-pointer"
          >
            Kapat
          </button>
          <a
            href={partner.website}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="whitespace-nowrap flex items-center justify-center gap-2 px-6 py-3 bg-[#FFFFFF] border border-[#E2E8F0] text-[#334155] text-xs font-semibold tracking-widest uppercase hover:border-[#3B82F6] hover:text-[#0F172A] transition-all duration-300 cursor-pointer"
          >
            <i className="ri-external-link-line"></i>
            Web Sitesi
          </a>
        </div>
      </div>
    </div>
  );
}
